import { useEffect, useRef, useState } from "react";
import "./IssueActions.css";
import { Button } from "./Button";
import { Alert } from "./Card";
import { Input } from "./Field";
import { STATUS, STATUS_KEYS, type StatusKey } from "./Status";
import type { ChildrenReadyResult, Issue, IssueActsApi } from "../lib/tasks";
import { errText, handToNaru, isHeld, issueActsApi, needsSpec } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 이슈 상세의 조작 줄 ══
   채택 시안 Figma 28:2(t-mvqow5lsc21y). b3x-pr 상세의 조작(단계 · 담당 해제 · 취소 · 넘기기 ·
   세션 멈춤 · 삭제 · 프롬프트 복사)을 옮겼다.

   **판정은 서버가 한다.** 여기서 단추를 막는 것은 「누르면 거절될 것이 뻔하고, 사람이 먼저
   할 일이 따로 있는」 셋뿐이다 — 도는 세션 · 에픽 · held 삭제. 막을 때는 단추를 숨기지 않고
   비활성 + 이유 문장으로 말한다(색만으로 가르지 않는다). 그 밖의 거절은 서버 문장을 그대로
   보인다. */

/** 「단계 옮기기」 메뉴에 싣는 단계 — 취소는 뺀다(확인창을 거치는 따로 된 단추다).
 *  **전이표가 아니다** — 허락은 서버가 하고(`POST /tasks/:id/status`), 거절은 그 문장을 그대로 보인다.
 *  b3x-pr 상세의 「단계」 메뉴와 같은 자리다(채택 시안 Figma 33:2). */
const MOVE_TO: StatusKey[] = STATUS_KEYS.filter((k) => k !== "cancelled");

const CLOSED: StatusKey[] = ["done", "cancelled"];

/** 「하위 전부 대기로」가 건너뛴 이유 — 서버는 이름만 주고 문장은 여기서 쓴다. 모르는 이름은
 *  서버 문장(`message`) → 이름 그대로 순서로 보인다(조용히 버리지 않는다). */
const SKIP_KEY = {
  "already-ready": "issueActs.skip.alreadyReady",
  "not-startable": "issueActs.skip.notStartable",
  closed: "issueActs.skip.closed",
} as const;

export function childrenReadyText(r: ChildrenReadyResult): string {
  const why = new Map<string, number>();
  for (const s of r.skipped) {
    const k = s.reason && s.reason in SKIP_KEY ? tx(SKIP_KEY[s.reason as keyof typeof SKIP_KEY]) : (s.message ?? s.reason ?? "?");
    why.set(k, (why.get(k) ?? 0) + 1);
  }
  const parts = [tx("issueActs.childrenMoved", { n: r.moved.length })];
  for (const [k, n] of why) parts.push(tx("issueActs.childrenSkipped", { n, why: k }));
  if (r.openDeps.length) parts.push(tx("issueActs.childrenDeps", { n: r.openDeps.length }));
  return parts.join(" · ");
}

type Mode = "idle" | "cancel" | "delete" | "handoff" | "move";

export function IssueActions({
  t,
  me,
  api = issueActsApi,
  onChanged,
  onDeleted,
  initialMode = "idle",
}: {
  t: Issue;
  /** 들어온 사람의 이름 — 「내가 맡기」가 쓴다. 모르면 그 단추를 안 그린다. */
  me?: string;
  api?: IssueActsApi;
  onChanged: () => void;
  onDeleted: () => void;
  /** 패턴 페이지가 확인창 · 넘기기 창을 펼친 채로 보이려고 쓴다. */
  initialMode?: Mode;
}) {
  const [mode, setMode] = useState<Mode>(initialMode === "move" ? "idle" : initialMode);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [stopArmed, setStopArmed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [target, setTarget] = useState<"naru" | "name">("naru");
  const [name, setName] = useState("");

  const running = t.openSession !== null;
  const parent = t.childCount > 0;
  const closed = CLOSED.includes(t.status);
  const held = isHeld(t);
  const isReq = t.agentRequestedAt !== null;
  /** 기획 배차 — 스펙이 빈 할 일 · 대기. 대기로 올리고 나루에게 넘기면 기획 트랙으로 읽힌다. */
  const canPlan = !parent && !closed && !isReq && (t.status === "todo" || t.status === "ready") && needsSpec(t);
  /** 리뷰 다시 — 리뷰에서 막혔거나, 검토인데 자동 실행이 꺼진 것. 판정은 서버가 다시 한다. */
  const canReview =
    !parent && ((t.status === "blocked" && t.blockedFrom === "review") || (t.status === "review" && !isReq));

  const toNaru = () => handToNaru(api, t);

  async function run(key: string, f: () => Promise<unknown>, after: () => void = onChanged) {
    setBusy(key);
    setErr(null);
    try {
      await f();
      setMode("idle");
      after();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(null);
    }
  }

  async function copyPrompt() {
    setBusy("prompt");
    setErr(null);
    try {
      const p = await api.prompt(t.id, "run");
      await navigator.clipboard.writeText(p.prompt);
      setCopied(true);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(null);
    }
  }

  // 이유는 하나만 말한다 — 사람이 먼저 할 일의 순서대로.
  const why = running
    ? tx("issueActs.why.running")
    : parent
      ? tx("issueActs.why.parent")
      : held
        ? tx("issueActs.why.heldDelete")
        : null;

  if (mode === "cancel" || mode === "delete") {
    const isCancel = mode === "cancel";
    return (
      <div className="iacts__panel" role="group" aria-label={tx(isCancel ? "issueActs.cancelAsk" : "issueActs.deleteAsk")}>
        <strong>{tx(isCancel ? "issueActs.cancelAsk" : "issueActs.deleteAsk")}</strong>
        <p className="muted">
          {isCancel
            ? tx(t.assignee ? "issueActs.cancelBodyAssignee" : "issueActs.cancelBody", { id: t.id, who: t.assignee ?? "" })
            : tx(parent ? "issueActs.deleteBodyChildren" : "issueActs.deleteBody", { id: t.id, n: t.childCount })}
        </p>
        {err ? <Alert tone="error">{err}</Alert> : null}
        <div className="iacts__row">
          <Button onClick={() => setMode("idle")} disabled={busy !== null}>
            {tx("common.action.close")}
          </Button>
          <Button
            variant="danger"
            loading={busy === mode}
            onClick={() =>
              isCancel
                ? run("cancel", () => api.setStatus(t.id, "cancelled"))
                : run("delete", () => api.remove(t.id, parent), onDeleted)
            }
          >
            {tx(isCancel ? "issueActs.cancelGo" : "common.action.delete")}
          </Button>
        </div>
      </div>
    );
  }

  if (mode === "handoff") {
    const trimmed = name.trim();
    return (
      <div className="iacts__panel" role="group" aria-label={tx("issueActs.handoff")}>
        <strong>{tx("issueActs.handoff")}</strong>
        <div className="iacts__choices" role="radiogroup">
          <label className={`iacts__choice${target === "naru" ? " is-on" : ""}`}>
            <input type="radio" name={`handoff-${t.id}`} checked={target === "naru"} onChange={() => setTarget("naru")} />
            <span>
              <b>{tx("issueActs.toNaru")}</b>
              <span className="muted">{tx("issueActs.toNaruBody")}</span>
            </span>
          </label>
          <label className={`iacts__choice${target === "name" ? " is-on" : ""}`}>
            <input type="radio" name={`handoff-${t.id}`} checked={target === "name"} onChange={() => setTarget("name")} />
            <span>
              <b>{tx("issueActs.toName")}</b>
              <span className="muted">{tx("issueActs.toNameBody")}</span>
            </span>
          </label>
        </div>
        {target === "name" ? (
          <Input
            aria-label={tx("issueActs.namePlaceholder")}
            placeholder={tx("issueActs.namePlaceholder")}
            value={name}
            maxLength={64}
            onChange={(e) => setName(e.target.value)}
          />
        ) : null}
        {t.assignee ? (
          <p className="iacts__now">
            {tx("issueActs.nowAssignee", {
              who: t.assignee,
              kind: tx(held ? "board.detail.held" : "board.detail.ticket"),
            })}
          </p>
        ) : null}
        {err ? <Alert tone="error">{err}</Alert> : null}
        <div className="iacts__row">
          <Button onClick={() => setMode("idle")} disabled={busy !== null}>
            {tx("common.action.close")}
          </Button>
          <Button
            variant="primary"
            loading={busy === "handoff"}
            disabled={target === "name" && !trimmed}
            onClick={() =>
              run("handoff", async () => {
                if (target === "name") {
                  await api.setAssignee(t.id, trimmed);
                  return;
                }
                await toNaru();
              })
            }
          >
            {tx("issueActs.handoffGo")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="iacts">
      {why ? <p className="iacts__why">{why}</p> : null}
      {err ? <Alert tone="error">{err}</Alert> : null}
      {copied ? <p className="iacts__note">{tx("issueActs.copied")}</p> : null}
      {result ? <p className="iacts__note">{result}</p> : null}
      <div className="iacts__row" aria-label={tx("issueActs.label")}>
        {!parent ? (
          <StepMenu
            current={t.status}
            busy={busy}
            disabled={busy !== null || running}
            initialOpen={initialMode === "move"}
            onPick={(to) => run(`step-${to}`, () => api.setStatus(t.id, to))}
          />
        ) : null}
        {canReview ? (
          <Button
            variant="primary"
            loading={busy === "review"}
            disabled={busy !== null || running}
            onClick={() => run("review", () => api.reviewRequest(t.id))}
          >
            ↻ {tx("issueActs.reviewRerun")}
          </Button>
        ) : null}
        {canPlan ? (
          <Button
            variant="primary"
            loading={busy === "plan"}
            disabled={busy !== null || running}
            onClick={() => run("plan", toNaru)}
          >
            + {tx("issueActs.planRun")}
          </Button>
        ) : null}
        {parent && !closed ? (
          <Button
            loading={busy === "children"}
            disabled={busy !== null}
            onClick={() =>
              run("children", async () => {
                setResult(childrenReadyText(await api.childrenReady(t.id)));
              })
            }
          >
            {tx("issueActs.childrenReady")}
          </Button>
        ) : null}
        {t.agentRequestedAt ? (
          <Button
            loading={busy === "unrequest"}
            disabled={busy !== null}
            onClick={() => run("unrequest", () => api.agentRequest(t.id, false))}
          >
            {tx("issueActs.unrequest")}
          </Button>
        ) : (
          <Button variant="primary" disabled={busy !== null || running || closed} onClick={() => setMode("handoff")}>
            → {tx("issueActs.handoff")}
          </Button>
        )}
        {t.assignee ? (
          <Button
            loading={busy === "unassign"}
            disabled={busy !== null || running}
            onClick={() => run("unassign", () => api.setAssignee(t.id, null))}
          >
            {tx("issueActs.unassign")}
          </Button>
        ) : me && !closed && !parent ? (
          <Button
            loading={busy === "assignMe"}
            disabled={busy !== null}
            onClick={() => run("assignMe", () => api.setAssignee(t.id, me))}
          >
            {tx("issueActs.assignMe")}
          </Button>
        ) : null}
        {!parent ? (
          <Button loading={busy === "prompt"} disabled={busy !== null} onClick={copyPrompt}>
            ⧉ {tx("issueActs.copyPrompt")}
          </Button>
        ) : null}
        {running ? (
          <Button
            variant="danger"
            loading={busy === "stop"}
            disabled={busy !== null}
            onClick={() =>
              stopArmed
                ? run("stop", () => api.stopSession(t.openSession!.id), () => {
                    setStopArmed(false);
                    onChanged();
                  })
                : setStopArmed(true)
            }
          >
            ■ {tx(stopArmed ? "issueActs.stopArmed" : "issueActs.stop")}
          </Button>
        ) : null}
        <span className="iacts__gap" />
        {!closed ? (
          <Button variant="danger" disabled={busy !== null || running || parent} onClick={() => setMode("cancel")}>
            ⊘ {tx("issueActs.cancel")}
          </Button>
        ) : null}
        <Button variant="danger" disabled={busy !== null || running || held} onClick={() => setMode("delete")}>
          {tx("common.action.delete")}
        </Button>
      </div>
    </div>
  );
}

/** 「단계 옮기기 ▾」 — 공통 `.b-menu`. 지금 단계는 꺼진 항목으로 남겨 「어디서 옮기나」를 보인다. */
export function StepMenu({
  current,
  busy,
  disabled,
  initialOpen = false,
  onPick,
}: {
  current: StatusKey;
  busy: string | null;
  disabled: boolean;
  /** 패턴 페이지가 열린 채로 보이려고 쓴다. */
  initialOpen?: boolean;
  onPick: (to: StatusKey) => void;
}) {
  const [open, setOpen] = useState(initialOpen);
  // 판은 `.is-fixed` 로 띄운다 — 대화상자의 overflow 에 잘리지 않게(공통 「…」 행 메뉴와 같은 길).
  // 자리는 단추에서 잰다. 패턴 페이지처럼 처음부터 열린 판은 제자리(absolute)에 둔다.
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  function toggle() {
    if (open) return setOpen(false);
    const r = wrap.current?.getBoundingClientRect();
    setAt(r ? { top: r.bottom + 4, left: r.left } : null);
    setOpen(true);
  }
  useEffect(() => {
    if (!open || initialOpen) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    // 메뉴만 닫는다 — 바깥 대화상자의 Esc 까지 가지 않게 잡아 둔다.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    // 띄운 판은 단추를 안 따라간다 — 대화상자를 굴리면 닫는다(엉뚱한 자리에 남지 않게).
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [open, initialOpen]);
  const moving = busy?.startsWith("step-") ?? false;
  return (
    <div className="b-menu-wrap" ref={wrap}>
      <Button
        variant="primary"
        loading={moving}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        {tx("issueActs.move")} ▾
      </Button>
      {open ? (
        <div
          className={`b-menu is-start${at ? " is-fixed" : ""}`}
          style={at ? { top: at.top, left: at.left } : undefined}
          role="menu"
          aria-label={tx("issueActs.move")}
        >
          {MOVE_TO.map((k) => (
            <button
              key={k}
              type="button"
              role="menuitem"
              className="b-menu-item"
              disabled={k === current}
              onClick={() => {
                setOpen(false);
                onPick(k);
              }}
            >
              <span aria-hidden="true">{STATUS[k].glyph}</span>
              {k === current ? tx("issueActs.moveCurrent", { label: STATUS[k].label }) : STATUS[k].label}
            </button>
          ))}
          <p className="b-menu-note">{tx("issueActs.moveNote")}</p>
        </div>
      ) : null}
    </div>
  );
}
